// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useRef, useState } from 'react';

import { useI18n } from '@/i18n';

type UseDeleteAccountParams = {
  apiBaseUrl: string;
  userId?: string | null;
  token?: string | null;
  /** Called once the server confirms the account is gone, so the caller can end the session. */
  onDeleted?: () => void | Promise<void>;
};

const DELETE_REQUEST_TIMEOUT_MS = 20000;

/**
 * Deletes the signed-in account over HTTP rather than the BFF socket: the socket is torn down along
 * with the session that follows, and a request that has to survive that is easier to reason about
 * as a plain call with its own timeout.
 */
export const useDeleteAccount = ({ apiBaseUrl, userId, token, onDeleted }: UseDeleteAccountParams) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
  }, []);

  const deleteAccount = useCallback(async () => {
    if (inFlightRef.current) return false;
    if (!userId || !token) {
      setError(t('profile.deleteAccountFailed'));
      return false;
    }

    inFlightRef.current = true;
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DELETE_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${apiBaseUrl}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      // A missing user means the account is already gone, which is the outcome the caller wanted.
      if (!response.ok && response.status !== 404) {
        throw new Error(`delete failed ${response.status}`);
      }

      await onDeleted?.();
      return true;
    } catch (err) {
      console.warn('[profile] account delete failed', err);
      setError(t('profile.deleteAccountFailed'));
      return false;
    } finally {
      clearTimeout(timeout);
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [apiBaseUrl, onDeleted, t, token, userId]);

  return { deleteAccount, loading, error, reset };
};
